// src/features/tickets/components/TicketSidePanel.tsx
//
// Kontext-Panel rechts neben dem Ticket-Thread.
// Zeigt Kundendaten (bzw. bei offener Gesellschaft die Firmengruppe, bei
// Tickets ohne Kunde die Zuordnungsvorschlaege), den zustaendigen Mitarbeiter,
// die Fristen des Kunden (F5) und Ticket-Metadaten. Der Kundenname oeffnet das
// Stammdatenfenster des Kunden direkt auf dem Reiter "Tickets" (alle Tickets
// und Zuordnungsregeln). Weiterer Ausbau (DATEV-Sync, offene Rechnungen,
// Personalnummern-Suche) folgt.

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Mail, Phone, User as UserIcon, Info, Users } from 'lucide-react';
import { EmailSenderIdentity } from '../../../services/email/types';
import { TicketDetailView, TicketStatus, TicketKundenKontext } from '../types';
import { formatDate } from '../../../shared/utils/dateUtils';
import { useCustomerDeadlines, CustomerDeadlineItem, DeadlineState } from '../hooks/useCustomerDeadlines';
import { useCustomerTickets, CustomerTicketSummary } from '../hooks/useCustomerTickets';
import { useDomainTickets, DomainTicketSummary } from '../hooks/useDomainTickets';
import { EditCustomerModal } from '../../customers/components/EditCustomerModal';

interface TicketSidePanelProps {
  ticket: TicketDetailView;
  staffList: EmailSenderIdentity[];
  /** Zuordnungskontext (Gruppe, Vorschlaege); optional. */
  kundenKontext?: TicketKundenKontext | null;
  /** Oeffnet das Zuordnungsfenster, optional mit vorausgewaehltem Kunden. */
  onOpenCustomerAssign?: (preselectCustomerId?: string) => void;
  /** Wird aufgerufen, wenn im Stammdatenfenster des Kunden etwas gespeichert wurde. */
  onCustomerUpdated?: () => void;
}

const SOURCE_LABELS: Record<string, string> = {
  intranet: 'Intranet',
  email: 'E-Mail',
  system: 'System'
};

const MONTH_NAMES = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
];

const TICKET_STATUS_BADGE: Record<TicketStatus, { label: string; color: string; bg: string }> = {
  new:      { label: 'Neu',         color: '#1D4ED8', bg: '#EFF6FF' },
  open:     { label: 'Offen',       color: '#047857', bg: '#ECFDF5' },
  pending:  { label: 'Wartet',      color: '#B45309', bg: '#FFFBEB' },
  resolved: { label: 'Gelöst',      color: '#6B7280', bg: '#F3F4F6' },
  closed:   { label: 'Geschlossen', color: '#6B7280', bg: '#F3F4F6' }
};

export const TicketSidePanel: React.FC<TicketSidePanelProps> = ({ ticket, staffList, kundenKontext, onOpenCustomerAssign, onCustomerUpdated }) => {
  const navigate = useNavigate();
  // Stammdatenfenster des Kunden (startet auf dem Reiter "Tickets")
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const customer = ticket.customer;
  const customerGroup = ticket.customer_group ?? null;
  const { loading: deadlinesLoading, workingMonth, items: deadlineItems } = useCustomerDeadlines(customer);
  const { loading: ticketsLoading, tickets: customerTickets } = useCustomerTickets(customer?.id ?? null, ticket.id);
  const { loading: domainTicketsLoading, tickets: domainTickets } = useDomainTickets(customer?.id ?? null);

  // Zustaendigen Mitarbeiter ueber die Staff-Liste aufloesen (customer traegt nur
  // die ID). Bei offener Gesellschaft gilt der Zustaendige der Firmengruppe.
  const zustaendigerId = customer
    ? customer.zustaendiger_mitarbeiter_id
    : (kundenKontext?.gruppe?.zustaendiger_mitarbeiter_id ?? null);
  const zustaendiger = zustaendigerId
    ? staffList.find(s => s.id === zustaendigerId)
    : undefined;

  const ansprechpartner = customer
    ? [customer.vorname, customer.nachname].filter(Boolean).join(' ').trim()
    : '';

  const vorschlaege = !customer ? (kundenKontext?.vorschlaege ?? []) : [];

  return (
    <div style={{
      width: '320px',
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: '16px'
    }}>
      {/* Kunde */}
      <PanelSection title="Kunde">
        {customer ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <InfoRow
              icon={<Building2 size={15} />}
              value={customer.company}
              bold
              onClick={() => setShowCustomerModal(true)}
              title="Kunde öffnen: alle Tickets und Zuordnungsregeln"
            />
            {ansprechpartner && (
              <InfoRow icon={<UserIcon size={15} />} value={ansprechpartner} />
            )}
            {customer.email && (
              <InfoRow icon={<Mail size={15} />} value={customer.email} />
            )}
            {customer.telefon && (
              <InfoRow icon={<Phone size={15} />} value={customer.telefon} />
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {customerGroup ? (
              <>
                <InfoRow icon={<Users size={15} />} value={`Firmengruppe ${customerGroup.name}`} bold />
                <div style={{ fontSize: '13px', color: '#6B7280' }}>
                  Gesellschaft noch offen
                </div>
              </>
            ) : (
              <div style={{ fontSize: '13px', color: '#9CA3AF' }}>
                Keinem Kunden zugeordnet (Pool)
              </div>
            )}

            {vorschlaege.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div style={{ fontSize: '12px', color: '#6B7280' }}>Vorschläge</div>
                {vorschlaege.map(v => (
                  <button
                    key={v.customer_id}
                    type="button"
                    onClick={() => onOpenCustomerAssign?.(v.customer_id)}
                    disabled={!onOpenCustomerAssign}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      cursor: onOpenCustomerAssign ? 'pointer' : 'default',
                      backgroundColor: '#F9FAFB',
                      border: '1px solid #E5E7EB',
                      borderRadius: '6px',
                      padding: '6px 10px',
                      fontSize: '13px',
                      color: '#111827',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {v.company}
                  </button>
                ))}
              </div>
            )}

            {onOpenCustomerAssign && (
              <button
                type="button"
                onClick={() => onOpenCustomerAssign()}
                style={{
                  alignSelf: 'flex-start',
                  padding: '6px 12px',
                  backgroundColor: 'white',
                  color: '#1D4ED8',
                  border: '1px solid #BFDBFE',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                Kunde zuordnen
              </button>
            )}
          </div>
        )}
      </PanelSection>

      {/* Zustaendiger Mitarbeiter */}
      {zustaendiger && (
        <PanelSection title="Zuständiger Mitarbeiter">
          <InfoRow icon={<UserIcon size={15} />} value={zustaendiger.fullName} />
        </PanelSection>
      )}

      {/* Fristen des Kunden (read-only, gespiegelt aus dem Fristen-Dashboard) */}
      {customer && (
        <PanelSection title="Fristen">
          {deadlinesLoading ? (
            <div style={{ fontSize: '13px', color: '#9CA3AF' }}>Lädt…</div>
          ) : deadlineItems.length === 0 ? (
            <div style={{ fontSize: '13px', color: '#9CA3AF' }}>
              Keine Fristen für diesen Monat.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {workingMonth && (
                <div style={{ fontSize: '12px', color: '#6B7280' }}>
                  Arbeitsmonat: {MONTH_NAMES[workingMonth.month - 1]} {workingMonth.year}
                </div>
              )}
              {deadlineItems.map(item => (
                <DeadlineRow key={item.key} item={item} />
              ))}
            </div>
          )}
        </PanelSection>
      )}

      {/* Weitere Tickets dieses Kunden (F6, read-only) */}
      {customer && (
        <PanelSection title="Weitere Tickets dieses Kunden">
          {ticketsLoading ? (
            <div style={{ fontSize: '13px', color: '#9CA3AF' }}>Lädt…</div>
          ) : customerTickets.length === 0 ? (
            <div style={{ fontSize: '13px', color: '#9CA3AF' }}>
              Keine weiteren Tickets dieses Kunden.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {customerTickets.map(t => (
                <CustomerTicketRow
                  key={t.id}
                  ticket={t}
                  onOpen={() => navigate(`/tickets/${t.id}`)}
                />
              ))}
            </div>
          )}
          <button
            type="button"
            onClick={() => setShowCustomerModal(true)}
            style={{
              marginTop: '10px',
              padding: 0,
              background: 'none',
              border: 'none',
              color: '#1D4ED8',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer'
            }}
          >
            Alle Tickets dieses Kunden anzeigen
          </button>
        </PanelSection>
      )}

      {/* Weitere Tickets gleicher Domain (F6 Teil 2, read-only, ueber RPC) */}
      {customer && (domainTicketsLoading || domainTickets.length > 0) && (
        <PanelSection title="Weitere Tickets gleicher Domain">
          {domainTicketsLoading ? (
            <div style={{ fontSize: '13px', color: '#9CA3AF' }}>Lädt…</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {domainTickets.map(t => (
                <DomainTicketRow
                  key={t.id}
                  ticket={t}
                  onOpen={() => navigate(`/tickets/${t.id}`)}
                />
              ))}
            </div>
          )}
        </PanelSection>
      )}

      {/* Ticket-Metadaten */}
      <PanelSection title="Ticket-Details">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <MetaRow label="Nummer" value={ticket.ticket_number} mono />
          <MetaRow label="Quelle" value={SOURCE_LABELS[ticket.source] || ticket.source} />
          <MetaRow label="Erstellt" value={formatDateTime(ticket.created_at)} />
          {ticket.closed_at && (
            <MetaRow label="Geschlossen" value={formatDateTime(ticket.closed_at)} />
          )}
        </div>
      </PanelSection>

      {/* Platzhalter-Hinweis fuer Stufe-4-Integrationen */}
      <div style={{
        padding: '12px',
        backgroundColor: '#F9FAFB',
        border: '1px dashed #D1D5DB',
        borderRadius: '8px',
        display: 'flex',
        gap: '8px',
        alignItems: 'flex-start'
      }}>
        <Info size={15} color="#9CA3AF" style={{ marginTop: '1px', flexShrink: 0 }} />
        <div style={{ fontSize: '12px', color: '#6B7280', lineHeight: '1.5' }}>
          DATEV-Sync und offene Rechnungen werden hier in einer
          späteren Ausbaustufe angezeigt.
        </div>
      </div>

      {/* Stammdatenfenster des Kunden, startet auf dem Reiter "Tickets" */}
      {customer && showCustomerModal && (
        <EditCustomerModal
          isOpen={showCustomerModal}
          customer={customer}
          initialTab="tickets"
          onClose={() => setShowCustomerModal(false)}
          onSuccess={() => { onCustomerUpdated?.(); }}
        />
      )}
    </div>
  );
};

// ============================================
// Panel-Section (Card mit Titel)
// ============================================

interface PanelSectionProps {
  title: string;
  children: React.ReactNode;
}

const PanelSection: React.FC<PanelSectionProps> = ({ title, children }) => (
  <div style={{
    backgroundColor: 'white',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    padding: '14px 16px'
  }}>
    <div style={{
      fontSize: '11px',
      fontWeight: '600',
      color: '#9CA3AF',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      marginBottom: '10px'
    }}>
      {title}
    </div>
    {children}
  </div>
);

// ============================================
// Info-Row mit Icon
// ============================================

interface InfoRowProps {
  icon: React.ReactNode;
  value: string;
  bold?: boolean;
  /** Macht den Wert anklickbar (als Link dargestellt). */
  onClick?: () => void;
  title?: string;
}

const InfoRow: React.FC<InfoRowProps> = ({ icon, value, bold, onClick, title }) => (
  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
    <span style={{ color: '#9CA3AF', flexShrink: 0, display: 'inline-flex' }}>
      {icon}
    </span>
    {onClick ? (
      <button
        type="button"
        onClick={onClick}
        title={title}
        style={{
          padding: 0,
          background: 'none',
          border: 'none',
          textAlign: 'left',
          fontSize: '13px',
          color: '#1D4ED8',
          fontWeight: bold ? '600' : '400',
          wordBreak: 'break-word',
          cursor: 'pointer'
        }}
      >
        {value}
      </button>
    ) : (
      <span style={{
        fontSize: '13px',
        color: '#111827',
        fontWeight: bold ? '600' : '400',
        wordBreak: 'break-word'
      }}>
        {value}
      </span>
    )}
  </div>
);

// ============================================
// Meta-Row (Label + Value)
// ============================================

interface MetaRowProps {
  label: string;
  value: string;
  mono?: boolean;
}

const MetaRow: React.FC<MetaRowProps> = ({ label, value, mono }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
    <span style={{ fontSize: '13px', color: '#6B7280' }}>{label}</span>
    <span style={{
      fontSize: '13px',
      color: '#111827',
      fontWeight: '500',
      fontFamily: mono ? 'monospace' : 'inherit',
      textAlign: 'right'
    }}>
      {value}
    </span>
  </div>
);

// ============================================
// Fristen-Zeile
// ============================================

const DEADLINE_STATE_STYLE: Record<DeadlineState, { label: string; color: string; bg: string }> = {
  overdue:   { label: 'überfällig',     color: '#B91C1C', bg: '#FEF2F2' },
  today:     { label: 'heute fällig',   color: '#B45309', bg: '#FFFBEB' },
  upcoming:  { label: 'offen',          color: '#1D4ED8', bg: '#EFF6FF' },
  completed: { label: 'erledigt',       color: '#047857', bg: '#ECFDF5' },
  snoozed:   { label: 'zurückgestellt', color: '#6B7280', bg: '#F3F4F6' },
  wartet_auf_kunden: { label: 'wartet auf Kunden', color: '#92400E', bg: '#FEF3C7' }
};

const DeadlineRow: React.FC<{ item: CustomerDeadlineItem }> = ({ item }) => {
  const s = DEADLINE_STATE_STYLE[item.state];
  const done = item.state === 'completed';
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
      <div style={{ minWidth: 0 }}>
        <div style={{
          fontSize: '13px',
          color: done ? '#9CA3AF' : '#111827',
          fontWeight: '500',
          textDecoration: done ? 'line-through' : 'none',
          wordBreak: 'break-word'
        }}>
          {item.label}
        </div>
        <div style={{ fontSize: '12px', color: '#6B7280' }}>{formatDate(item.date)}</div>
      </div>
      <span style={{
        flexShrink: 0,
        fontSize: '11px',
        fontWeight: '600',
        color: s.color,
        backgroundColor: s.bg,
        padding: '2px 8px',
        borderRadius: '9999px',
        whiteSpace: 'nowrap'
      }}>
        {s.label}
      </span>
    </div>
  );
};

// ============================================
// Weitere-Tickets-Zeile
// ============================================

const CustomerTicketRow: React.FC<{ ticket: CustomerTicketSummary; onOpen: () => void }> = ({ ticket, onOpen }) => {
  const badge = TICKET_STATUS_BADGE[ticket.status] || { label: ticket.status, color: '#6B7280', bg: '#F3F4F6' };
  return (
    <button
      type="button"
      onClick={onOpen}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '10px',
        width: '100%',
        textAlign: 'left',
        cursor: 'pointer',
        backgroundColor: '#F9FAFB',
        border: '1px solid #E5E7EB',
        borderRadius: '6px',
        padding: '8px 10px'
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{
          fontSize: '13px',
          color: '#111827',
          fontWeight: '500',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {ticket.subject}
        </div>
        <div style={{ fontSize: '11px', color: '#9CA3AF', fontFamily: 'monospace' }}>
          {ticket.ticket_number} · {formatDate(new Date(ticket.last_activity))}
        </div>
      </div>
      <span style={{
        flexShrink: 0,
        fontSize: '11px',
        fontWeight: '600',
        color: badge.color,
        backgroundColor: badge.bg,
        padding: '2px 8px',
        borderRadius: '9999px',
        whiteSpace: 'nowrap'
      }}>
        {badge.label}
      </span>
    </button>
  );
};

// ============================================
// Domain-Tickets-Zeile (anderer Kunde gleicher Domain)
// ============================================

const DomainTicketRow: React.FC<{ ticket: DomainTicketSummary; onOpen: () => void }> = ({ ticket, onOpen }) => {
  const badge = TICKET_STATUS_BADGE[ticket.status] || { label: ticket.status, color: '#6B7280', bg: '#F3F4F6' };
  return (
    <button
      type="button"
      onClick={onOpen}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '10px',
        width: '100%',
        textAlign: 'left',
        cursor: 'pointer',
        backgroundColor: '#F9FAFB',
        border: '1px solid #E5E7EB',
        borderRadius: '6px',
        padding: '8px 10px'
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{
          fontSize: '13px',
          color: '#111827',
          fontWeight: '500',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {ticket.company || 'Unbekannter Kunde'}
        </div>
        <div style={{
          fontSize: '12px',
          color: '#6B7280',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {ticket.subject}
        </div>
        <div style={{ fontSize: '11px', color: '#9CA3AF', fontFamily: 'monospace' }}>
          {ticket.ticket_number} · {formatDate(new Date(ticket.last_activity))}
        </div>
      </div>
      <span style={{
        flexShrink: 0,
        fontSize: '11px',
        fontWeight: '600',
        color: badge.color,
        backgroundColor: badge.bg,
        padding: '2px 8px',
        borderRadius: '9999px',
        whiteSpace: 'nowrap'
      }}>
        {badge.label}
      </span>
    </button>
  );
};

// ============================================
// Helper
// ============================================

function formatDateTime(isoTimestamp: string | null): string {
  if (!isoTimestamp) return '—';
  const date = new Date(isoTimestamp);
  if (Number.isNaN(date.getTime())) return '—';
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${dd}.${mm}.${yyyy}, ${hh}:${min}`;
}
